import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import CustomizedHook from './CustomizedHook';
import ClientProvider from './client';
export default createDemo(import.meta.url, CustomizedHook, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/material/components/autocomplete/demos/customized-hook/CustomizedHook.tsx",
    "fileName": "CustomizedHook.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import useAutocomplete from '@mui/material/useAutocomplete';\nimport type {\n  AutocompleteGetItemProps,\n  UseAutocompleteProps,\n} from '@mui/material/useAutocomplete';\nimport CheckIcon from '@mui/icons-material/Check';\nimport CloseIcon from '@mui/icons-material/Close';\nimport { styled } from '@mui/material/styles';\nimport { autocompleteClasses } from '@mui/material/Autocomplete';\n\nconst Root = styled('div')(({ theme }) => ({\n  color: 'rgba(0,0,0,0.85)',\n  fontSize: '14px',\n  ...theme.applyStyles('dark', {\n    color: 'rgba(255,255,255,0.65)',\n  }),\n}));\n\nconst Label = styled('label')`\n  padding: 0 0 4px;\n  line-height: 1.5;\n  display: block;\n`;\n\nconst InputWrapper = styled('div')(({ theme }) => ({\n  width: '300px',\n  border: '1px solid #d9d9d9',\n  backgroundColor: '#fff',\n  borderRadius: '4px',\n  padding: '1px',\n  display: 'flex',\n  flexWrap: 'wrap',\n  ...theme.applyStyles('dark', {\n    borderColor: '#434343',\n    backgroundColor: '#141414',\n  }),\n  '&:hover': {\n    borderColor: '#40a9ff',\n    ...theme.applyStyles('dark', {\n      borderColor: '#177ddc',\n    }),\n  },\n  '&.focused': {\n    borderColor: '#40a9ff',\n    boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',\n    ...theme.applyStyles('dark', {\n      borderColor: '#177ddc',\n    }),\n  },\n  '& input': {\n    backgroundColor: '#fff',\n    color: 'rgba(0,0,0,.85)',\n    height: '30px',\n    boxSizing: 'border-box',\n    padding: '4px 6px',\n    width: '0',\n    minWidth: '30px',\n    flexGrow: 1,\n    border: 0,\n    margin: 0,\n    outline: 0,\n    ...theme.applyStyles('dark', {\n      color: 'rgba(255,255,255,0.65)',\n      backgroundColor: '#141414',\n    }),\n  },\n}));\n\ninterface ItemProps extends ReturnType<AutocompleteGetItemProps<true>> {\n  label: string;\n}\n\nfunction Item(props: ItemProps) {\n  const { label, onDelete, ...other } = props;\n  return (\n    <div {...other}>\n      <span>{label}</span>\n      <CloseIcon onClick={onDelete} />\n    </div>\n  );\n}\n\nconst StyledItem = styled(Item)<ItemProps>(({ theme }) => ({\n  display: 'flex',\n  alignItems: 'center',\n  height: '24px',\n  margin: '2px',\n  lineHeight: '22px',\n  backgroundColor: '#fafafa',\n  border: `1px solid #e8e8e8`,\n  borderRadius: '2px',\n  boxSizing: 'content-box',\n  padding: '0 4px 0 10px',\n  outline: 0,\n  overflow: 'hidden',\n  ...theme.applyStyles('dark', {\n    backgroundColor: 'rgba(255,255,255,0.08)',\n    borderColor: '#303030',\n  }),\n  '&:focus': {\n    borderColor: '#40a9ff',\n    backgroundColor: '#e6f7ff',\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#003b57',\n      borderColor: '#177ddc',\n    }),\n  },\n  '& span': {\n    overflow: 'hidden',\n    whiteSpace: 'nowrap',\n    textOverflow: 'ellipsis',\n  },\n  '& svg': {\n    fontSize: '12px',\n    cursor: 'pointer',\n    padding: '4px',\n  },\n}));\n\nconst Listbox = styled('ul')(({ theme }) => ({\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  width: '300px',\n  margin: '2px 0 0',\n  padding: 0,\n  position: 'absolute',\n  listStyle: 'none',\n  backgroundColor: '#fff',\n  overflow: 'auto',\n  maxHeight: '250px',\n  borderRadius: '4px',\n  boxShadow: '0 2px 8px rgb(0 0 0 / 0.15)',\n  zIndex: 1,\n  ...theme.applyStyles('dark', {\n    backgroundColor: '#141414',\n  }),\n  '& li': {\n    padding: '5px 12px',\n    display: 'flex',\n    '& span': {\n      flexGrow: 1,\n    },\n    '& svg': {\n      color: 'transparent',\n    },\n  },\n  \"& li[aria-selected='true']\": {\n    backgroundColor: '#fafafa',\n    fontWeight: 600,\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#2b2b2b',\n    }),\n    '& svg': {\n      color: '#1890ff',\n    },\n  },\n  [`& li.${autocompleteClasses.focused}`]: {\n    backgroundColor: '#e6f7ff',\n    cursor: 'pointer',\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#003b57',\n    }),\n    '& svg': {\n      color: 'currentColor',\n    },\n  },\n}));\n\nfunction CustomAutocomplete<Value>(\n  props: UseAutocompleteProps<Value, true, false, false>,\n) {\n  const {\n    getRootProps,\n    getInputLabelProps,\n    getInputProps,\n    getItemProps,\n    getListboxProps,\n    getOptionProps,\n    groupedOptions,\n    value,\n    focused,\n    setAnchorEl,\n  } = useAutocomplete({\n    multiple: true,\n    ...props,\n  });\n\n  return (\n    <Root>\n      <div {...getRootProps()}>\n        <Label {...getInputLabelProps()}>Customized hook</Label>\n        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>\n          {value.map((option, index) => {\n            const { key, ...itemProps } = getItemProps({ index });\n            return (\n              <StyledItem\n                key={key}\n                {...itemProps}\n                label={props.getOptionLabel!(option)}\n              />\n            );\n          })}\n          <input {...getInputProps()} />\n        </InputWrapper>\n      </div>\n      {groupedOptions.length > 0 ? (\n        <Listbox {...getListboxProps()}>\n          {groupedOptions.map((option, index) => {\n            const { key, ...optionProps } = getOptionProps({ option, index });\n            return (\n              <li key={key} {...optionProps}>\n                <span>{props.getOptionLabel!(option)}</span>\n                <CheckIcon fontSize=\"small\" />\n              </li>\n            );\n          })}\n        </Listbox>\n      ) : null}\n    </Root>\n  );\n}\n\nexport default function CustomizedHook() {\n  return (\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 2,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    <CustomAutocomplete<FilmOptionType>\n      id=\"customized-hook-demo\"\n      defaultValue={[top100Films[1]]}\n      options={top100Films}\n      getOptionLabel={(option) => option.title}\n    />\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  );\n}\n\ninterface FilmOptionType {\n  title: string;\n  year: number;\n}\n\n// Top 100 films as rated by IMDb users. http://www.imdb.com/chart/top\nconst top100Films = [\n  { title: 'The Shawshank Redemption', year: 1994 },\n  { title: 'The Godfather', year: 1972 },\n  { title: 'The Godfather: Part II', year: 1974 },\n  { title: 'The Dark Knight', year: 2008 },\n  { title: '12 Angry Men', year: 1957 },\n  { title: \"Schindler's List\", year: 1993 },\n  { title: 'Pulp Fiction', year: 1994 },\n  {\n    title: 'The Lord of the Rings: The Return of the King',\n    year: 2003,\n  },\n  { title: 'The Good, the Bad and the Ugly', year: 1966 },\n  { title: 'Fight Club', year: 1999 },\n  {\n    title: 'The Lord of the Rings: The Fellowship of the Ring',\n    year: 2001,\n  },\n  {\n    title: 'Star Wars: Episode V - The Empire Strikes Back',\n    year: 1980,\n  },\n  { title: 'Forrest Gump', year: 1994 },\n  { title: 'Inception', year: 2010 },\n  {\n    title: 'The Lord of the Rings: The Two Towers',\n    year: 2002,\n  },\n  { title: \"One Flew Over the Cuckoo's Nest\", year: 1975 },\n  { title: 'Goodfellas', year: 1990 },\n  { title: 'The Matrix', year: 1999 },\n  { title: 'Seven Samurai', year: 1954 },\n  {\n    title: 'Star Wars: Episode IV - A New Hope',\n    year: 1977,\n  },\n  { title: 'City of God', year: 2002 },\n  { title: 'Se7en', year: 1995 },\n  { title: 'The Silence of the Lambs', year: 1991 },\n  { title: \"It's a Wonderful Life\", year: 1946 },\n  { title: 'Life Is Beautiful', year: 1997 },\n  { title: 'The Usual Suspects', year: 1995 },\n  { title: 'Léon: The Professional', year: 1994 },\n  { title: 'Spirited Away', year: 2001 },\n  { title: 'Saving Private Ryan', year: 1998 },\n  { title: 'Once Upon a Time in the West', year: 1968 },\n  { title: 'American History X', year: 1998 },\n  { title: 'Interstellar', year: 2014 },\n  { title: 'Casablanca', year: 1942 },\n  { title: 'City Lights', year: 1931 },\n  { title: 'Psycho', year: 1960 },\n  { title: 'The Green Mile', year: 1999 },\n  { title: 'The Intouchables', year: 2011 },\n  { title: 'Modern Times', year: 1936 },\n  { title: 'Raiders of the Lost Ark', year: 1981 },\n  { title: 'Rear Window', year: 1954 },\n  { title: 'The Pianist', year: 2002 },\n  { title: 'The Departed', year: 2006 },\n  { title: 'Terminator 2: Judgment Day', year: 1991 },\n  { title: 'Back to the Future', year: 1985 },\n  { title: 'Whiplash', year: 2014 },\n  { title: 'Gladiator', year: 2000 },\n  { title: 'Memento', year: 2000 },\n  { title: 'The Prestige', year: 2006 },\n  { title: 'The Lion King', year: 1994 },\n  { title: 'Apocalypse Now', year: 1979 },\n  { title: 'Alien', year: 1979 },\n  { title: 'Sunset Boulevard', year: 1950 },\n  {\n    title: 'Dr. Strangelove or: How I Learned to Stop Worrying and Love the Bomb',\n    year: 1964,\n  },\n  { title: 'The Great Dictator', year: 1940 },\n  { title: 'Cinema Paradiso', year: 1988 },\n  { title: 'The Lives of Others', year: 2006 },\n  { title: 'Grave of the Fireflies', year: 1988 },\n  { title: 'Paths of Glory', year: 1957 },\n  { title: 'Django Unchained', year: 2012 },\n  { title: 'The Shining', year: 1980 },\n  { title: 'WALL·E', year: 2008 },\n  { title: 'American Beauty', year: 1999 },\n  { title: 'The Dark Knight Rises', year: 2012 },\n  { title: 'Princess Mononoke', year: 1997 },\n  { title: 'Aliens', year: 1986 },\n  { title: 'Oldboy', year: 2003 },\n  { title: 'Once Upon a Time in America', year: 1984 },\n  { title: 'Witness for the Prosecution', year: 1957 },\n  { title: 'Das Boot', year: 1981 },\n  { title: 'Citizen Kane', year: 1941 },\n  { title: 'North by Northwest', year: 1959 },\n  { title: 'Vertigo', year: 1958 },\n  {\n    title: 'Star Wars: Episode VI - Return of the Jedi',\n    year: 1983,\n  },\n  { title: 'Reservoir Dogs', year: 1992 },\n  { title: 'Braveheart', year: 1995 },\n  { title: 'M', year: 1931 },\n  { title: 'Requiem for a Dream', year: 2000 },\n  { title: 'Amélie', year: 2001 },\n  { title: 'A Clockwork Orange', year: 1971 },\n  { title: 'Like Stars on Earth', year: 2007 },\n  { title: 'Taxi Driver', year: 1976 },\n  { title: 'Lawrence of Arabia', year: 1962 },\n  { title: 'Double Indemnity', year: 1944 },\n  {\n    title: 'Eternal Sunshine of the Spotless Mind',\n    year: 2004,\n  },\n  { title: 'Amadeus', year: 1984 },\n  { title: 'To Kill a Mockingbird', year: 1962 },\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  { title: 'Toy Story 3', year: 2010 },\n  { title: 'Logan', year: 2017 },\n  { title: 'Full Metal Jacket', year: 1987 },\n  { title: 'Dangal', year: 2016 },\n  { title: 'The Sting', year: 1973 },\n  { title: '2001: A Space Odyssey', year: 1968 },\n  { title: \"Singin' in the Rain\", year: 1952 },\n  { title: 'Toy Story', year: 1995 },\n  { title: 'Bicycle Thieves', year: 1948 },\n  { title: 'The Kid', year: 1921 },\n  { title: 'Inglourious Basterds', year: 2009 },\n  { title: 'Snatch', year: 2000 },\n  { title: '3 Idiots', year: 2009 },\n  { title: 'Monty Python and the Holy Grail', year: 1975 },\n];\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 363,
        "focusedLines": 6,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import useAutocomplete from '@mui/material/useAutocomplete';\nimport type {\n  AutocompleteGetItemProps,\n  UseAutocompleteProps,\n} from '@mui/material/useAutocomplete';\nimport CheckIcon from '@mui/icons-material/Check';\nimport CloseIcon from '@mui/icons-material/Close';\nimport { styled } from '@mui/material/styles';\nimport { autocompleteClasses } from '@mui/material/Autocomplete';\n\nconst Root = styled('div')(({ theme }) => ({\n  color: 'rgba(0,0,0,0.85)',\n  fontSize: '14px',\n  ...theme.applyStyles('dark', {\n    color: 'rgba(255,255,255,0.65)',\n  }),\n}));\n\nconst Label = styled('label')`\n  padding: 0 0 4px;\n  line-height: 1.5;\n  display: block;\n`;\n\nconst InputWrapper = styled('div')(({ theme }) => ({\n  width: '300px',\n  border: '1px solid #d9d9d9',\n  backgroundColor: '#fff',\n  borderRadius: '4px',\n  padding: '1px',\n  display: 'flex',\n  flexWrap: 'wrap',\n  ...theme.applyStyles('dark', {\n    borderColor: '#434343',\n    backgroundColor: '#141414',\n  }),\n  '&:hover': {\n    borderColor: '#40a9ff',\n    ...theme.applyStyles('dark', {\n      borderColor: '#177ddc',\n    }),\n  },\n  '&.focused': {\n    borderColor: '#40a9ff',\n    boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',\n    ...theme.applyStyles('dark', {\n      borderColor: '#177ddc',\n    }),\n  },\n  '& input': {\n    backgroundColor: '#fff',\n    color: 'rgba(0,0,0,.85)',\n    height: '30px',\n    boxSizing: 'border-box',\n    padding: '4px 6px',\n    width: '0',\n    minWidth: '30px',\n    flexGrow: 1,\n    border: 0,\n    margin: 0,\n    outline: 0,\n    ...theme.applyStyles('dark', {\n      color: 'rgba(255,255,255,0.65)',\n      backgroundColor: '#141414',\n    }),\n  },\n}));\n\ninterface ItemProps extends ReturnType<AutocompleteGetItemProps<true>> {\n  label: string;\n}\n\nfunction Item(props: ItemProps) {\n  const { label, onDelete, ...other } = props;\n  return (\n    <div {...other}>\n      <span>{label}</span>\n      <CloseIcon onClick={onDelete} />\n    </div>\n  );\n}\n\nconst StyledItem = styled(Item)<ItemProps>(({ theme }) => ({\n  display: 'flex',\n  alignItems: 'center',\n  height: '24px',\n  margin: '2px',\n  lineHeight: '22px',\n  backgroundColor: '#fafafa',\n  border: `1px solid #e8e8e8`,\n  borderRadius: '2px',\n  boxSizing: 'content-box',\n  padding: '0 4px 0 10px',\n  outline: 0,\n  overflow: 'hidden',\n  ...theme.applyStyles('dark', {\n    backgroundColor: 'rgba(255,255,255,0.08)',\n    borderColor: '#303030',\n  }),\n  '&:focus': {\n    borderColor: '#40a9ff',\n    backgroundColor: '#e6f7ff',\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#003b57',\n      borderColor: '#177ddc',\n    }),\n  },\n  '& span': {\n    overflow: 'hidden',\n    whiteSpace: 'nowrap',\n    textOverflow: 'ellipsis',\n  },\n  '& svg': {\n    fontSize: '12px',\n    cursor: 'pointer',\n    padding: '4px',\n  },\n}));\n\nconst Listbox = styled('ul')(({ theme }) => ({\n"
      ],
      [
        "span",
        "frame",
        "  width: '300px',\n  margin: '2px 0 0',\n  padding: 0,\n  position: 'absolute',\n  listStyle: 'none',\n  backgroundColor: '#fff',\n  overflow: 'auto',\n  maxHeight: '250px',\n  borderRadius: '4px',\n  boxShadow: '0 2px 8px rgb(0 0 0 / 0.15)',\n  zIndex: 1,\n  ...theme.applyStyles('dark', {\n    backgroundColor: '#141414',\n  }),\n  '& li': {\n    padding: '5px 12px',\n    display: 'flex',\n    '& span': {\n      flexGrow: 1,\n    },\n    '& svg': {\n      color: 'transparent',\n    },\n  },\n  \"& li[aria-selected='true']\": {\n    backgroundColor: '#fafafa',\n    fontWeight: 600,\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#2b2b2b',\n    }),\n    '& svg': {\n      color: '#1890ff',\n    },\n  },\n  [`& li.${autocompleteClasses.focused}`]: {\n    backgroundColor: '#e6f7ff',\n    cursor: 'pointer',\n    ...theme.applyStyles('dark', {\n      backgroundColor: '#003b57',\n    }),\n    '& svg': {\n      color: 'currentColor',\n    },\n  },\n}));\n\nfunction CustomAutocomplete<Value>(\n  props: UseAutocompleteProps<Value, true, false, false>,\n) {\n  const {\n    getRootProps,\n    getInputLabelProps,\n    getInputProps,\n    getItemProps,\n    getListboxProps,\n    getOptionProps,\n    groupedOptions,\n    value,\n    focused,\n    setAnchorEl,\n  } = useAutocomplete({\n    multiple: true,\n    ...props,\n  });\n\n  return (\n    <Root>\n      <div {...getRootProps()}>\n        <Label {...getInputLabelProps()}>Customized hook</Label>\n        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>\n          {value.map((option, index) => {\n            const { key, ...itemProps } = getItemProps({ index });\n            return (\n              <StyledItem\n                key={key}\n                {...itemProps}\n                label={props.getOptionLabel!(option)}\n              />\n            );\n          })}\n          <input {...getInputProps()} />\n        </InputWrapper>\n      </div>\n      {groupedOptions.length > 0 ? (\n        <Listbox {...getListboxProps()}>\n          {groupedOptions.map((option, index) => {\n            const { key, ...optionProps } = getOptionProps({ option, index });\n            return (\n              <li key={key} {...optionProps}>\n                <span>{props.getOptionLabel!(option)}</span>\n                <CheckIcon fontSize=\"small\" />\n              </li>\n            );\n          })}\n        </Listbox>\n      ) : null}\n    </Root>\n  );\n}\n\nexport default function CustomizedHook() {\n  return (\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        "    <CustomAutocomplete<FilmOptionType>\n      id=\"customized-hook-demo\"\n      defaultValue={[top100Films[1]]}\n      options={top100Films}\n      getOptionLabel={(option) => option.title}\n    />\n"
      ],
      [
        "span",
        "frame",
        "  );\n}\n\ninterface FilmOptionType {\n  title: string;\n  year: number;\n}\n\n// Top 100 films as rated by IMDb users. http://www.imdb.com/chart/top\nconst top100Films = [\n  { title: 'The Shawshank Redemption', year: 1994 },\n  { title: 'The Godfather', year: 1972 },\n  { title: 'The Godfather: Part II', year: 1974 },\n  { title: 'The Dark Knight', year: 2008 },\n  { title: '12 Angry Men', year: 1957 },\n  { title: \"Schindler's List\", year: 1993 },\n  { title: 'Pulp Fiction', year: 1994 },\n  {\n    title: 'The Lord of the Rings: The Return of the King',\n    year: 2003,\n  },\n  { title: 'The Good, the Bad and the Ugly', year: 1966 },\n  { title: 'Fight Club', year: 1999 },\n  {\n    title: 'The Lord of the Rings: The Fellowship of the Ring',\n    year: 2001,\n  },\n  {\n    title: 'Star Wars: Episode V - The Empire Strikes Back',\n    year: 1980,\n  },\n  { title: 'Forrest Gump', year: 1994 },\n  { title: 'Inception', year: 2010 },\n  {\n    title: 'The Lord of the Rings: The Two Towers',\n    year: 2002,\n  },\n  { title: \"One Flew Over the Cuckoo's Nest\", year: 1975 },\n  { title: 'Goodfellas', year: 1990 },\n  { title: 'The Matrix', year: 1999 },\n  { title: 'Seven Samurai', year: 1954 },\n  {\n    title: 'Star Wars: Episode IV - A New Hope',\n    year: 1977,\n  },\n  { title: 'City of God', year: 2002 },\n  { title: 'Se7en', year: 1995 },\n  { title: 'The Silence of the Lambs', year: 1991 },\n  { title: \"It's a Wonderful Life\", year: 1946 },\n  { title: 'Life Is Beautiful', year: 1997 },\n  { title: 'The Usual Suspects', year: 1995 },\n  { title: 'Léon: The Professional', year: 1994 },\n  { title: 'Spirited Away', year: 2001 },\n  { title: 'Saving Private Ryan', year: 1998 },\n  { title: 'Once Upon a Time in the West', year: 1968 },\n  { title: 'American History X', year: 1998 },\n  { title: 'Interstellar', year: 2014 },\n  { title: 'Casablanca', year: 1942 },\n  { title: 'City Lights', year: 1931 },\n  { title: 'Psycho', year: 1960 },\n  { title: 'The Green Mile', year: 1999 },\n  { title: 'The Intouchables', year: 2011 },\n  { title: 'Modern Times', year: 1936 },\n  { title: 'Raiders of the Lost Ark', year: 1981 },\n  { title: 'Rear Window', year: 1954 },\n  { title: 'The Pianist', year: 2002 },\n  { title: 'The Departed', year: 2006 },\n  { title: 'Terminator 2: Judgment Day', year: 1991 },\n  { title: 'Back to the Future', year: 1985 },\n  { title: 'Whiplash', year: 2014 },\n  { title: 'Gladiator', year: 2000 },\n  { title: 'Memento', year: 2000 },\n  { title: 'The Prestige', year: 2006 },\n  { title: 'The Lion King', year: 1994 },\n  { title: 'Apocalypse Now', year: 1979 },\n  { title: 'Alien', year: 1979 },\n  { title: 'Sunset Boulevard', year: 1950 },\n  {\n    title: 'Dr. Strangelove or: How I Learned to Stop Worrying and Love the Bomb',\n    year: 1964,\n  },\n  { title: 'The Great Dictator', year: 1940 },\n  { title: 'Cinema Paradiso', year: 1988 },\n  { title: 'The Lives of Others', year: 2006 },\n  { title: 'Grave of the Fireflies', year: 1988 },\n  { title: 'Paths of Glory', year: 1957 },\n  { title: 'Django Unchained', year: 2012 },\n  { title: 'The Shining', year: 1980 },\n  { title: 'WALL·E', year: 2008 },\n  { title: 'American Beauty', year: 1999 },\n  { title: 'The Dark Knight Rises', year: 2012 },\n  { title: 'Princess Mononoke', year: 1997 },\n  { title: 'Aliens', year: 1986 },\n  { title: 'Oldboy', year: 2003 },\n  { title: 'Once Upon a Time in America', year: 1984 },\n  { title: 'Witness for the Prosecution', year: 1957 },\n  { title: 'Das Boot', year: 1981 },\n  { title: 'Citizen Kane', year: 1941 },\n  { title: 'North by Northwest', year: 1959 },\n  { title: 'Vertigo', year: 1958 },\n  {\n    title: 'Star Wars: Episode VI - Return of the Jedi',\n    year: 1983,\n  },\n  { title: 'Reservoir Dogs', year: 1992 },\n  { title: 'Braveheart', year: 1995 },\n  { title: 'M', year: 1931 },\n  { title: 'Requiem for a Dream', year: 2000 },\n  { title: 'Amélie', year: 2001 },\n  { title: 'A Clockwork Orange', year: 1971 },\n  { title: 'Like Stars on Earth', year: 2007 },\n  { title: 'Taxi Driver', year: 1976 },\n  { title: 'Lawrence of Arabia', year: 1962 },\n  { title: 'Double Indemnity', year: 1944 },\n  {\n    title: 'Eternal Sunshine of the Spotless Mind',\n    year: 2004,\n  },\n  { title: 'Amadeus', year: 1984 },\n  { title: 'To Kill a Mockingbird', year: 1962 },\n"
      ],
      [
        "span",
        "frame",
        "  { title: 'Toy Story 3', year: 2010 },\n  { title: 'Logan', year: 2017 },\n  { title: 'Full Metal Jacket', year: 1987 },\n  { title: 'Dangal', year: 2016 },\n  { title: 'The Sting', year: 1973 },\n  { title: '2001: A Space Odyssey', year: 1968 },\n  { title: \"Singin' in the Rain\", year: 1952 },\n  { title: 'Toy Story', year: 1995 },\n  { title: 'Bicycle Thieves', year: 1948 },\n  { title: 'The Kid', year: 1921 },\n  { title: 'Inglourious Basterds', year: 2009 },\n  { title: 'Snatch', year: 2000 },\n  { title: '3 Idiots', year: 2009 },\n  { title: 'Monty Python and the Holy Grail', year: 1975 },\n];\n"
      ]
    ],
    "fallbackCritical": {
      "2": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 223
            },
            [
              "    <",
              [
                "span",
                "pl-c1 di-jsx",
                "CustomAutocomplete"
              ],
              "<",
              [
                "span",
                "pl-en",
                "FilmOptionType"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 224
            },
            [
              "      ",
              [
                "span",
                "pl-e",
                "id"
              ],
              [
                "span",
                "pl-k di-pu",
                "="
              ],
              [
                "span",
                "pl-s",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "customized-hook-demo",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 225
            },
            [
              "      ",
              [
                "span",
                "pl-e",
                "defaultValue"
              ],
              [
                "span",
                "pl-k di-pu",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              "[",
              [
                "span",
                "pl-smi",
                "top100Films"
              ],
              "[",
              [
                "span",
                "pl-c1 di-num",
                "1"
              ],
              "]]",
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 226
            },
            [
              "      ",
              [
                "span",
                "pl-e",
                "options"
              ],
              [
                "span",
                "pl-k di-pu",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi",
                "top100Films"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 227
            },
            [
              "      ",
              [
                "span",
                "pl-e",
                "getOptionLabel"
              ],
              [
                "span",
                "pl-k di-pu",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              "(",
              [
                "span",
                "pl-v",
                "option"
              ],
              ") ",
              [
                "span",
                "pl-k di-pu",
                "=>"
              ],
              " ",
              [
                "span",
                "pl-smi",
                "option"
              ],
              ".",
              [
                "span",
                "pl-c1",
                "title"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 228
            },
            "    />"
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 363,
    "focusedLines": 6,
    "collapsible": true,
    "externals": [
      "@mui/material/useAutocomplete",
      "@mui/icons-material/Check",
      "@mui/icons-material/Close",
      "@mui/material/styles",
      "@mui/material/Autocomplete"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
